// ─────────────────────────────────────────────────────────
// Einstellungen → Layout & Textvorlagen → Automatische Anhänge
// ─────────────────────────────────────────────────────────
// plancraft-Modell (Karte „Eigene Anhänge"): Akkordeon über dieselben 8
// Dokumenttypen wie die Anfangs-/Schlusstexte, je Typ eine Liste hochgeladener
// Dateien (PDF/Bild), die beim Versand automatisch mitgehen — per E-Mail als
// weitere Anhänge, per WhatsApp als Folge-Nachrichten.
//
// plancrafts erste Karte („Titelübersicht"/„Aufmaß") fehlt bewusst: das sind
// von plancraft generierte Zusatzseiten, die es in LevelUpWerk nicht gibt.
//
// Prefix aa-. Rechte: layout:read / layout:write. Die Akkordeon-Klassen sind
// bewusst die der Anfangs-/Schlusstexte (cd-dt-acc-*) — gleiche Optik, kein Klon.
import { icon, escapeHtml } from '../../utils/dom.js';
import { uploadAttachment, deleteAttachment } from '../../core/org.js';
import { t } from '../../i18n/index.js';

// Der Server schickt {key, label} — wir übersetzen über den key und lassen sein
// deutsches label als Fallback stehen (funktioniert also auch ohne Backend-Änderung).
const docTypeLabel = (key, serverLabel) => {
  const v = t('docTypes.' + key);
  return v && v !== 'docTypes.' + key ? v : serverLabel;
};

const ICON_FOR_EXT = { pdf: 'picture_as_pdf', png: 'image', jpg: 'image' };

function data(s) { return s.aaData || {}; }
function typesOf(s) { return data(s).docTypes || []; }
function filesOf(s, type) {
  const all = data(s).attachments || {};
  return Array.isArray(all[type]) ? all[type] : [];
}

function humanSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// ── Rendering ─────────────────────────────────────────────
function renderType(s, type, label, w) {
  const open = s.aaOpen instanceof Set && s.aaOpen.has(type);
  const list = filesOf(s, type);
  const chev = open ? 'expand_more' : 'chevron_right';
  const busy = s.aaUploading === type;

  let body = '';
  if (open) {
    const rows = list.map((f) => `
      <div class="cd-aa-file">
        <span class="cd-aa-file-ic">${icon(ICON_FOR_EXT[f.ext] || 'draft', 18)}</span>
        <button class="cd-aa-file-name" data-action="aa-open" data-id="${escapeHtml(f.id)}" title="${escapeHtml(t('settings.attachments.viewFile'))}">${escapeHtml(f.fileName)}</button>
        <span class="cd-aa-file-size">${escapeHtml(humanSize(f.size))}</span>
        ${w ? `<button class="cd-dt-del" data-action="aa-del" data-id="${escapeHtml(f.id)}" title="${escapeHtml(t('settings.attachments.removeFile'))}">${icon('close', 16)}</button>` : ''}
      </div>`).join('');
    const add = w ? `
      <button class="cd-aa-add" data-action="aa-pick" data-type="${type}" ${busy ? 'disabled' : ''}>
        ${escapeHtml(busy ? t('settings.attachments.uploading') : t('settings.attachments.upload'))}
      </button>` : '';
    body = `<div class="cd-dt-acc-body">${rows}${add}</div>`;
  }

  return `
    <div class="cd-dt-acc-row ${open ? 'open' : ''}">
      <button class="cd-dt-acc-head" data-action="aa-toggle" data-type="${type}">
        <span class="cd-dt-chev">${icon(chev, 18)}</span>
        <span class="cd-dt-acc-label">${escapeHtml(docTypeLabel(type, label))}</span>
        ${list.length ? `<span class="cd-dt-badge">${list.length}</span>` : ''}
      </button>
      ${body}
    </div>`;
}

export function renderAttachmentsSection(ctx) {
  const { s, can } = ctx;
  if (!can('layout:read')) return `<div class="cd-set-empty">${escapeHtml(t('common.noPermission'))}</div>`;
  if (!(s.aaOpen instanceof Set)) s.aaOpen = new Set();
  const w = can('layout:write');

  const rows = typesOf(s).map((t) => renderType(s, t.key, t.label, w)).join('');

  return `
    <div class="cd-emp">
      <div class="cd-emp-card">
        <div class="cd-emp-card-head">${icon('attach_file', 18)}<h3>${escapeHtml(t('settings.attachments.title'))}</h3></div>
        <div class="cd-dt-acc">${rows || `<div class="cd-set-empty">${escapeHtml(t('common.loading'))}</div>`}</div>
      </div>
      ${s.aaError ? `<div class="cd-un-err">${escapeHtml(s.aaError)}</div>` : ''}
      <div class="cd-emp-info cd-sc-note">${icon('info', 18)}<span>Diese Dateien gehen beim Versand automatisch mit — z.B. die AGB zu jedem Angebot. Per E-Mail hängen sie an derselben Nachricht; per WhatsApp folgen sie dem Dokument als einzelne Nachrichten, weil WhatsApp nur eine Datei pro Nachricht erlaubt. Aktuell verschickt LevelUpWerk Offerten (Angebot) und Rechnungen — die übrigen Typen sind Vorlagen für künftige Dokumentarten.</span></div>
    </div>`;
}

// ── Aktionen ──────────────────────────────────────────────
function pickFile(type, ctx) {
  const { s, draw, reload } = ctx;
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'application/pdf,image/png,image/jpeg';
  input.onchange = () => {
    const file = input.files && input.files[0];
    if (!file) return;
    s.aaError = ''; s.aaUploading = type; draw();
    uploadAttachment(type, file)
      .then(() => { s.aaUploading = null; return reload ? reload() : null; })
      .then(() => draw())
      .catch((err) => { s.aaUploading = null; s.aaError = err.message || t('settings.attachments.uploadFailed'); draw(); });
  };
  input.click();
}

export function handleAttachmentAction(a, el, ctx) {
  const { s, draw, reload } = ctx;
  if (!a || a.indexOf('aa-') !== 0) return false;

  if (a === 'aa-toggle') {
    const t = el.dataset.type;
    if (!(s.aaOpen instanceof Set)) s.aaOpen = new Set();
    if (s.aaOpen.has(t)) s.aaOpen.delete(t); else s.aaOpen.add(t);
    draw();
    return true;
  }
  if (a === 'aa-pick') {
    if (!ctx.can('layout:write')) return true;
    pickFile(el.dataset.type, ctx);
    return true;
  }
  if (a === 'aa-del') {
    if (!ctx.can('layout:write')) return true;
    s.aaError = ''; draw();
    deleteAttachment(el.dataset.id)
      .then(() => (reload ? reload() : null))
      .then(() => draw())
      .catch((err) => { s.aaError = err.message || t('settings.attachments.removeFailed'); draw(); });
    return true;
  }
  if (a === 'aa-open') {
    // Datei braucht Auth → als Blob holen und in neuem Tab zeigen
    openAttachment(el.dataset.id).catch(() => { s.aaError = t('settings.attachments.openFailed'); draw(); });
    return true;
  }
  return false;
}

async function openAttachment(id) {
  const { fetchAttachmentBlob } = await import('../../core/org.js');
  const url = await fetchAttachmentBlob(id);
  if (url) window.open(url, '_blank', 'noopener');
}
